/**
 * Onboarding Flow Hook
 *
 * Main orchestration hook that composes smaller, focused hooks.
 * Manages the overall onboarding flow state and coordinates actions.
 *
 * Supports different onboarding flow types via the optional `startApi` parameter.
 */

import { useState, useCallback, useRef } from 'react';
import * as api from '@/features/onboarding/api/onboarding-api';
import {
  defaultOnboardingApiClient,
  type OnboardingApiClient,
} from '@/features/onboarding/api/onboarding-api-client';
import { SMS_RESEND_COUNTDOWN_SECONDS } from '@/features/onboarding/constants/onboarding.constants';
import { initialOnboardingState } from '@/features/onboarding/hooks/onboarding-state';
import { getEnrolledFactors } from '@/features/onboarding/hooks/onboarding-utils';
import { useFactorActions } from '@/features/onboarding/hooks/use-factor-actions';
import { useJwtExchange } from '@/features/onboarding/hooks/use-jwt-exchange';
import { useOtpActions } from '@/features/onboarding/hooks/use-otp-actions';
import {
  FlowStatus,
  FactorType,
  type OnboardingState,
  type OnboardingFlowResponse,
} from '@/features/onboarding/types/onboarding.types';

interface UseOnboardingFlowOptions {
  /** Magic-link token (required to start flow) */
  token: string;
  /**
   * Custom start API function - allows different onboarding flows to use different endpoints
   * Defaults to standard user onboarding API
   */
  startApi?: (token: string) => Promise<OnboardingFlowResponse>;
  /**
   * Injectable API client used by the sub-hooks (submit/choose/complete/…).
   * Defaults to the standard user-onboarding endpoints. The
   * authenticator-enrollment-invitation flow passes its own BFF-backed client.
   */
  api?: OnboardingApiClient;
  /**
   * Override the post-submit auto-complete decision. See ``useFactorActions``
   * for the contract — wizards with a post-completion UX detour (device
   * pairing) return false to keep the onboarding session cookie alive until
   * the detour finishes.
   */
  shouldAutoComplete?: (response: OnboardingFlowResponse) => boolean;
}

/**
 * Hook for managing the onboarding flow
 *
 * @param options - Configuration options including token and optional custom startApi
 */
export function useOnboardingFlow(options: UseOnboardingFlowOptions | string) {
  // Support both old signature (string) and new signature (options object)
  const {
    token,
    startApi = api.startOnboardingFlow,
    api: apiClient = defaultOnboardingApiClient,
    shouldAutoComplete,
  } = typeof options === 'string'
    ? { token: options, shouldAutoComplete: undefined }
    : options;
  const [state, setState] = useState<OnboardingState>(initialOnboardingState);

  const completePromiseRef = useRef<Promise<string | null> | null>(null);
  const hasCalledCompleteRef = useRef(false);

  // ============================================================================
  // COMPLETE API (centralized)
  // ============================================================================

  /**
   * Centralized /complete API call - ensures only one call is made.
   * Multiple callers will await the same promise.
   * Returns the exchange token on success, null on error (allowing retry).
   */
  const callCompleteApi = useCallback(async (): Promise<string | null> => {
    // Already completed - return cached token
    if (hasCalledCompleteRef.current) {
      setState(prev => ({
        ...prev,
        flowStatus: FlowStatus.COMPLETED,
        isLoading: false,
      }));

      return state.exchangeToken;
    }

    // If call is already in progress, wait for it
    if (completePromiseRef.current !== null) {
      return completePromiseRef.current;
    }

    // Start the API call and store the promise
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    completePromiseRef.current = (async () => {
      try {
        const response = await apiClient.completeOnboarding();
        const token = response.exchangeToken ?? null;

        setState(prev => ({
          ...prev,
          flowStatus: FlowStatus.COMPLETED,
          exchangeToken: token ?? prev.exchangeToken,
          isLoading: false,
          error: null,
        }));

        hasCalledCompleteRef.current = true;
        completePromiseRef.current = null;

        return token;
      } catch (error) {
        completePromiseRef.current = null;

        setState(prev => ({
          ...prev,
          error:
            error instanceof Error
              ? error.message
              : 'Failed to complete onboarding',
          isLoading: false,
          // Don't transition to COMPLETED on error - stay in current state for retry
        }));

        return null;
      }
    })();

    return completePromiseRef.current;
  }, [apiClient, state.exchangeToken]);

  // ============================================================================
  // PROCESS BACKEND RESPONSE
  // ============================================================================

  /**
   * Process backend response and update state
   * This is the central place where we map backend response to frontend state
   *
   * IMPORTANT: When hasAlternative=true, this automatically calls the backend
   * /authenticator-alternatives API to fetch the list of options
   */
  const processResponse = useCallback(
    async (response: OnboardingFlowResponse) => {
      const factorType = response.currentStep?.factor_type ?? null;
      const responseFactors = response.factors ?? [];
      const newEnrolledFactors = getEnrolledFactors(responseFactors);

      // When hasAlternative=true, fetch alternatives from backend
      let alternatives = response.alternatives ?? [];

      if (response.hasAlternative && alternatives.length === 0) {
        try {
          const altResponse = await apiClient.getAuthenticatorAlternatives();
          alternatives = altResponse.alternatives.map(alt => ({
            factorType: alt.factorType as FactorType, // Backend already returns factorType
            isEnrolled: alt.isEnrolled,
            displayName: alt.displayName,
            description: alt.description,
          }));
        } catch (error) {
          // Log error but don't fail the flow - alternatives might be provided in response
          console.error('Failed to fetch authenticator alternatives:', error);
        }
      }

      setState(prev => {
        // Preserve enrolled factors if the new response doesn't include them
        // This handles cases where /complete returns minimal policy data
        const enrolledFactors =
          newEnrolledFactors.length > 0
            ? newEnrolledFactors
            : prev.enrolledFactors;

        return {
          ...prev,
          hasStarted: true,
          flowStatus: response.status,
          hasAlternative: response.hasAlternative,
          currentFactorType: factorType,
          currentStepMetadata: response.currentStep?.metadata ?? null,
          factors: responseFactors.length > 0 ? responseFactors : prev.factors,
          canComplete: response.canComplete ?? false,
          alternatives,
          enrolledFactors,
          // Extract phone number from metadata if present
          phoneNumberObfuscated:
            response.currentStep?.metadata?.phone_number_obfuscated ??
            prev.phoneNumberObfuscated,
          // Track primary factor type
          primaryFactorType:
            factorType === FactorType.PASSWORD
              ? FactorType.PASSWORD
              : prev.primaryFactorType,
          // Capture exchange token when flow completes
          exchangeToken: response.exchangeToken ?? null,
          isLoading: false,
          error: null,
          // Reset retry state on successful response to prevent stale lockout
          hasExceededRetries: false,
        };
      });
    },
    [apiClient]
  );

  // ============================================================================
  // COMPOSE SMALLER HOOKS
  // ============================================================================

  const otpActions = useOtpActions({
    state,
    setState,
    processResponse,
    api: apiClient,
  });
  const factorActions = useFactorActions({
    setState,
    processResponse,
    callCompleteApi,
    api: apiClient,
    shouldAutoComplete,
  });
  const jwtExchange = useJwtExchange({ state, setState });

  // ============================================================================
  // FLOW CONTROL ACTIONS
  // ============================================================================

  /**
   * Handle "Get Started" button click
   * Calls the configured start API and transitions to the step returned by backend
   */
  const handleGetStarted = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await startApi(token);

      // Start OTP countdown if first step is SMS or WhatsApp OTP
      const isOtpStep =
        response.currentStep?.factor_type === FactorType.SMS_OTP ||
        response.currentStep?.factor_type === FactorType.WHATSAPP_OTP;

      await processResponse(response);

      if (isOtpStep) {
        setState(prev => ({
          ...prev,
          smsResendCountdown: SMS_RESEND_COUNTDOWN_SECONDS,
        }));
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to start',
        isLoading: false,
      }));
    }
  }, [token, startApi, processResponse]);

  /**
   * Final completion - called from "Go to My Apps" button in the success summary
   * Calls /complete API if exchange token is not available, then transitions to COMPLETED.
   */
  const handleFinalComplete = useCallback(async () => {
    await callCompleteApi();
  }, [callCompleteApi]);

  /**
   * Legacy completion handler - kept for backwards compatibility
   * Parent component handles redirect
   */
  const handleComplete = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    // No API call needed - backend already marked flow as complete
    // JWT exchange should have already happened via exchangeForSession
    setState(prev => ({ ...prev, isLoading: false }));
  }, []);

  // ============================================================================
  // UTILITIES
  // ============================================================================

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const isComplete = state.flowStatus === FlowStatus.COMPLETED;

  // ============================================================================
  // RETURN COMBINED API
  // ============================================================================

  return {
    state,

    // Flow control
    handleGetStarted,
    handleComplete,
    handleFinalComplete,
    clearError,

    // OTP actions (from useOtpActions)
    submitSmsCode: otpActions.submitSmsCode,
    resendSmsCode: otpActions.resendSmsCode,
    canResendSms: otpActions.canResendSms,

    // Factor actions (from useFactorActions)
    submitPrimaryFactor: factorActions.submitPrimaryFactor,
    completeSecondaryFactors: factorActions.completeSecondaryFactors,
    submitFactor: factorActions.submitFactor,
    chooseAlternative: factorActions.chooseAlternative,
    goBackToAlternatives: factorActions.goBackToAlternatives,

    // JWT Exchange (from useJwtExchange)
    exchangeForSession: jwtExchange.exchangeForSession,
    canExchange: jwtExchange.canExchange,

    // Computed
    isComplete,
  };
}
